import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginShell from '../components/login/LoginShell';
import EmailStep from '../components/login/EmailStep';
import EnquiryModal from '../components/login/EnquiryModal';
import { CreatePasswordStep, PasswordStep } from '../components/login/PasswordSteps';
import { VerifyEmailStep } from '../components/login/VerifyEmailStep';
import { CheckInboxStep } from '../components/login/CheckInboxStep';
import { KnownCompanyCard } from '../components/login/KnownCompanyCard';
import { useCompanyPreview } from '../hooks/useCompanyPreview';
import { useLogin } from '../hooks/useLogin';
import { useAuth } from '../contexts/AuthContext';

/**
 * Login v2 (TASK-201) — navy split-screen from `design/Login v2.dc.html`:
 * hero panel left, canvas form side right. Auth flows (email steps + Google)
 * are unchanged; success lands on the dashboard (deep links respected).
 *
 * Invitation emails link to `/login?email=<address>&name=<full name>` (`name`
 * only when the admin entered one); `useLogin` prefills the email field, and
 * when the precheck opens create-password for that invite's address, the
 * Full name field too — still editable.
 */
const Login: React.FC = () => {
  const {
    step,
    userStatus,
    email, setEmail,
    password, setPassword,
    confirmPassword, setConfirmPassword,
    name, setName,
    isLoading,
    error,
    verification,
    passwordReset,
    resetState,
    backToPassword,
    postLoginPath,
    handleEmailSubmit,
    handlePasswordSubmit,
    handleCreatePasswordSubmit,
    handleGoogleLogin,
  } = useLogin();

  const { currentUser, backendUser } = useAuth();
  const navigate = useNavigate();

  // "Talk to our team" enquiry capture — works fully logged out. Success
  // lives inside the modal (persistent, user-dismissed — no toast).
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  // Sign-up only: the company Robbie already knows for this email's domain.
  const knownCompany = useCompanyPreview(email, step === 'create-password');

  // Both halves of the session, not just Firebase: an unverified sign-up holds
  // a Firebase user for a moment before it is signed out, and it must not be
  // mistaken for an authenticated visitor and bounced to the dashboard.
  useEffect(() => {
    if (currentUser && backendUser) {
      navigate(postLoginPath, { replace: true });
    }
  }, [currentUser, backendUser, navigate, postLoginPath]);

  const title =
    step === 'password'
      ? 'Welcome back!'
      : step === 'verify-email' || step === 'check-inbox'
        ? 'Check your inbox'
        : 'Hello!';

  // Create-password is reached two ways and the copy says which: an invite
  // waiting for this address, or an organization that has opened its email
  // domain (no invite involved). It never names the organization: the
  // precheck is public and does not say which one.
  const emailDomain = email.split('@')[1] || 'work email';
  const createAccountSubtitle =
    userStatus === 'domain'
      ? `Your ${emailDomain} address lets you join your team`
      : "You've been invited to join your team";

  const subtitle =
    step === 'email'
      ? 'Enter your work email to continue'
      : step === 'password'
        ? 'Enter your password to sign in'
        : step === 'verify-email'
          ? verification.linkFailed
            ? `We'll send a verification link to ${email}. Click it, then come back here and sign in.`
            : `We sent a verification link to ${email}. Click it, then come back here and sign in.`
          : step === 'check-inbox'
            ? `We sent a reset link to ${email}. It works once.`
            : createAccountSubtitle;

  return (
    <>
      <LoginShell
        title={title}
        subtitle={subtitle}
        error={error}
        footer={
          <p className="mt-[20px] text-center text-body-sm text-ink-muted max-md:text-chrome-text">
            New here?{' '}
            <button
              type="button"
              onClick={() => setEnquiryOpen(true)}
              className="font-semibold text-action hover:text-action-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action max-md:text-white max-md:underline max-md:underline-offset-2 max-md:hover:text-white"
            >
              Talk to our team
            </button>
          </p>
        }
      >
        {step === 'email' && (
          <EmailStep
            email={email}
            setEmail={setEmail}
            isLoading={isLoading}
            onSubmit={handleEmailSubmit}
            onGoogle={handleGoogleLogin}
          />
        )}
        {step === 'password' && (
          <PasswordStep
            email={email}
            password={password}
            setPassword={setPassword}
            isLoading={isLoading}
            onSubmit={handlePasswordSubmit}
            onBack={resetState}
            onForgotPassword={passwordReset.handleForgotPassword}
            forgotSending={passwordReset.sending}
          />
        )}
        {step === 'create-password' && knownCompany && <KnownCompanyCard company={knownCompany} />}
        {step === 'create-password' && (
          <CreatePasswordStep
            name={name}
            setName={setName}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            isLoading={isLoading}
            onSubmit={handleCreatePasswordSubmit}
            onBack={resetState}
          />
        )}
        {step === 'verify-email' && (
          <VerifyEmailStep
            isLoading={verification.sending}
            resendCooldown={verification.resendCooldown}
            notice={verification.notice}
            onResend={verification.handleResend}
            onBack={resetState}
          />
        )}
        {step === 'check-inbox' && (
          <CheckInboxStep
            sending={passwordReset.sending}
            resendCooldown={passwordReset.resendCooldown}
            notice={passwordReset.notice}
            onResend={passwordReset.handleResend}
            onBack={backToPassword}
          />
        )}
      </LoginShell>

      <EnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />
    </>
  );
};

export default Login;
